import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import { useCallback, useMemo } from 'react';

import { AMOUNTS } from 'constants/amounts';
import { PRODUCT_TYPES_VALUES } from 'constants/dictionaries';
import { PERIODS } from 'constants/periods';

import { sendSentryClientErrorOnce, useAppDispatch, useAppSelector } from 'shared/lib';

import type { TCreditPurposeFormData, TCreditPurposeFormFields, TProductTypeTabId } from 'entities/creditForm';
import { selectCreditPurposeInfoData, setCreditPurposeInfo, useDictionaries } from 'entities/creditForm';

import { PRODUCT_TYPE_TAB_ITEMS } from '../config';

import { getProductTypeTabById } from './getProductTypeTabById';
import { resolveActiveProductTypeTabId } from './resolveActiveProductTypeTabId';

export const useProductTypeSelection = () => {
  const dispatch = useAppDispatch();
  const { setValue } = useFormContext<TCreditPurposeFormFields>();
  const creditPurposeData = useAppSelector(selectCreditPurposeInfoData);
  const { purposes, periods } = useDictionaries(['purposes', 'periods']);

  const activeTabId = useMemo(() => resolveActiveProductTypeTabId(creditPurposeData), [creditPurposeData]);

  const onSelectTab = useCallback(
    (tabId: TProductTypeTabId) => {
      const tab = getProductTypeTabById(tabId);
      const purpose = purposes.find(({ value }) => value === tab.purpose) ?? creditPurposeData.purpose;

      if (!purpose) {
        sendSentryClientErrorOnce('useProductTypeSelection', 'Purpose is not available on tab select', {
          tabId,
          tabPurpose: tab.purpose,
        });

        return;
      }

      const updateData: Partial<TCreditPurposeFormData> = {
        purpose,
        selectedProductType: tab.selectedProductType,
      };

      setValue('purpose', purpose, { shouldDirty: true, shouldValidate: true });

      if (tab.selectedProductType === PRODUCT_TYPES_VALUES.CREDIT_CARD) {
        const defaultAmount = String(AMOUNTS.defaultVitrinsCardLimit);
        const defaultPeriod = periods.find(({ value }) => value === PERIODS.defaultCreditCardFormValue);

        setValue('amount', defaultAmount, { shouldDirty: true, shouldValidate: true });
        updateData.amount = defaultAmount;

        if (defaultPeriod) {
          setValue('period', defaultPeriod, { shouldDirty: true, shouldValidate: true });
          updateData.period = defaultPeriod;
        }
      }

      dispatch(
        setCreditPurposeInfo({
          data: updateData,
          isFullFilled: false,
          isShowProductTypeSelection: true,
        }),
      );
    },
    [creditPurposeData.purpose, dispatch, periods, purposes, setValue],
  );

  return {
    activeTabId,
    onSelectTab,
    tabs: PRODUCT_TYPE_TAB_ITEMS,
  };
};
